import { PaginatedResponse, PaginationParams } from '../types';
import { apiClient } from './api';

type PagePayload<T> = {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

export type PaymentRow = {
  id: string;
  orderId: string;
  orderNumber: string;
  provider: string;
  amount: number;
  status: string;
  authority: string;
  refId: string;
  cardPan: string;
  fee: number;
  gatewayStatus: string;
  gatewayPayload?: unknown;
  verifiedAt: string;
  createdAt: string;
  updatedAt: string;
  customerName: string;
  customerPhone: string;
};

export type ZarinpalUnverifiedItem = {
  authority: string;
  amountRial: number;
  amount: number;
  callbackUrl: string;
  referer: string;
  date: string;
  localPayment?: PaymentRow;
};

export type PaymentInquiryResult = {
  payment: PaymentRow;
  inquiry: {
    code: number;
    status: string;
    message: string;
    raw?: unknown;
  };
};

function unwrapPage<T>(res: { success: boolean; data?: PagePayload<T>; message?: string; error?: string }): PaginatedResponse<T> {
  if (!res.success || !res.data) {
    throw new Error(res.message || res.error || 'خطا در دریافت داده');
  }
  return {
    data: res.data.data ?? [],
    total: res.data.total ?? 0,
    page: res.data.page ?? 1,
    limit: res.data.limit ?? 10,
    totalPages: res.data.totalPages ?? 1,
  };
}

export const paymentStatusLabel: Record<string, string> = {
  pending: 'در انتظار',
  completed: 'موفق / تأییدشده',
  failed: 'ناموفق',
};

export const gatewayStatusLabel: Record<string, string> = {
  VERIFIED: 'تأیید شده',
  ALREADY_VERIFIED: 'قبلاً تأیید شده',
  PAID: 'پرداخت‌شده (منتظر verify)',
  IN_BANK: 'در بانک',
  FAILED: 'ناموفق',
  NOK: 'لغو / ناموفق',
};

export const paymentService = {
  async list(
    params: PaginationParams & { status?: string; provider?: string; q?: string },
  ): Promise<PaginatedResponse<PaymentRow>> {
    const res = await apiClient.get<PagePayload<PaymentRow>>('/admin/payments', {
      params: {
        page: params.page,
        limit: params.limit,
        status: params.status || undefined,
        provider: params.provider || undefined,
        q: params.q || undefined,
      },
    });
    return unwrapPage(res);
  },

  async get(id: string): Promise<PaymentRow> {
    const res = await apiClient.get<PaymentRow>(`/admin/payments/${id}`);
    if (!res.success || !res.data) throw new Error(res.message || 'پرداخت یافت نشد');
    return res.data;
  },

  async inquire(id: string): Promise<PaymentInquiryResult> {
    const res = await apiClient.post<PaymentInquiryResult>(`/admin/payments/${id}/inquire`);
    if (!res.success || !res.data) throw new Error(res.message || 'استعلام ناموفق بود');
    return res.data;
  },

  async verify(id: string): Promise<{ payment: PaymentRow; refId?: string; alreadyVerified?: boolean }> {
    const res = await apiClient.post<{ payment: PaymentRow; refId?: string; alreadyVerified?: boolean }>(
      `/admin/payments/${id}/verify`,
    );
    if (!res.success || !res.data) throw new Error(res.message || 'تأیید پرداخت ناموفق بود');
    return res.data;
  },

  async listUnverified(): Promise<ZarinpalUnverifiedItem[]> {
    const res = await apiClient.get<{ items: ZarinpalUnverifiedItem[]; total: number }>(
      '/admin/payments/zarinpal/unverified',
    );
    if (!res.success || !res.data) throw new Error(res.message || 'دریافت لیست تأییدنشده‌ها ناموفق بود');
    return res.data.items ?? [];
  },
};
